/**
 * Report browser-side failures to the backend access log.
 *
 * An uncaught exception or a crashed panel is only ever seen by the one
 * person whose browser hit it. Sending it to the same log the admin
 * console already reads makes a bad deploy visible from the server side.
 */

import { apiClient } from "./api/client";

/** How many reports one page load may send; a render loop must not flood the log. */
const MAX_REPORTS_PER_PAGE = 10;

const seen = new Set<string>();
let sent = 0;

export interface ReportedError {
  message: string;
  stack?: string;
  /** Where it was caught: "window", "promise", or a panel name. */
  source: string;
}

/** Turn whatever was thrown into a message and a stack. */
export function describeThrown(value: unknown): { message: string; stack?: string } {
  if (value instanceof Error) {
    return { message: value.message || value.name, stack: value.stack };
  }
  if (typeof value === "string") {
    return { message: value };
  }
  try {
    return { message: JSON.stringify(value) };
  } catch {
    return { message: String(value) };
  }
}

/**
 * Send one error to the backend, at most once per distinct message and at
 * most MAX_REPORTS_PER_PAGE times per page load.
 *
 * @returns true when a report was sent
 */
export function reportError(error: ReportedError): boolean {
  const key = `${error.source}:${error.message}`;
  if (seen.has(key) || sent >= MAX_REPORTS_PER_PAGE) {
    return false;
  }
  seen.add(key);
  sent += 1;
  apiClient.reportClientError(
    { message: `[${error.source}] ${error.message}`, stack: error.stack },
    window.location.pathname,
    window.sessionId
  );
  return true;
}

/** Forget what was reported (tests). */
export function resetErrorReporting(): void {
  seen.clear();
  sent = 0;
}

/** Hook window-level errors and unhandled promise rejections. */
export function installGlobalErrorReporting(target: Window = window): void {
  target.addEventListener("error", (event) => {
    const described = describeThrown(event.error ?? event.message);
    reportError({ ...described, source: "window" });
  });
  target.addEventListener("unhandledrejection", (event) => {
    const described = describeThrown(event.reason);
    reportError({ ...described, source: "promise" });
  });
}
